import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { classNames } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'accent' | 'destructive' | 'ghost' | 'link'
type Size = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
}

const variantStyles: Record<Variant, string> = {
  primary: 'bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-400',
  // Filled with the secondary brand color ("Rolla Blue") — same shape as `primary`, distinct
  // from the neutral outline `secondary` variant name below (that one predates this color).
  accent: 'bg-secondary-600 text-white hover:bg-secondary-700 disabled:bg-secondary-400',
  secondary: 'border border-gray-300 bg-white text-gray-700 hover:shadow disabled:bg-gray-100',
  destructive: 'border border-red-200 text-red-600 hover:bg-red-50 disabled:text-red-400',
  ghost: 'text-gray-700 hover:bg-gray-100 disabled:text-gray-400',
  link: 'text-primary-600 hover:text-primary-700 disabled:text-primary-400 underline-offset-4 hover:underline',
}

const sizeStyles: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-2.5 text-sm',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={classNames(
          'inline-flex items-center justify-center rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'
